import { useCallback, useMemo, useState } from 'react';
import useLabels from '../../../hooks/useLabels';
import { ListActionType } from '../../../ui/list-actions/ListActions';
import { Icons } from '../../../ui/icon/icons/material';
import { TestIds } from '../../../mocks/ids';
import { useErrorHandlerContext } from '../../../contexts/error-handler/ErrorContext';
import { Prompt } from '../models/PromptTypes';

export type PromptListActionType = Pick<ListActionType, 'onClick'> & Partial<Omit<ListActionType, 'onClick'>>;

export type PromptListActionsType = {
    add?: PromptListActionType;
    edit?: PromptListActionType;
    copy?: PromptListActionType;
    remove?: PromptListActionType;
};

export const usePromptListActions = ({ add, edit, copy, remove }: PromptListActionsType) => {
    const labels = useLabels();

    return [
        {
            label: labels.promptListAdd || 'Add',
            iconName: Icons.add,
            testId: TestIds.promptAddButton,
            isHidden: !add?.onClick,
            ...add
        },
        {
            label: labels.promptListEdit || 'Edit',
            iconName: Icons.edit,
            testId: TestIds.promptEditAddButton,
            isHidden: !edit?.onClick,
            ...edit
        },
        {
            label: labels.promptListCopy || 'Copy',
            iconName: Icons.copy,
            testId: TestIds.promptCopyButton,
            isHidden: !copy?.onClick,
            ...copy
        },
        {
            label: labels.promptListDelete || 'Delete',
            iconName: Icons.delete,
            testId: TestIds.promptDeleteButton,
            isHidden: !remove?.onClick,
            ...remove
        }
    ].filter(Boolean) as ListActionType[];
};

export const usePromptListState = ({ prompts }: { prompts: Prompt[]; }) => {
    const { removeError, registerError, errorId } = useErrorHandlerContext();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [loadingIds, setLoadingIds] = useState<string[]>([]);
    const handleErrorRemoval = useCallback(() => removeError(errorId), [errorId, removeError]);

    const handleErrorRegistering = useCallback((headline: string, description: string, details?: any) => {
        registerError({ [errorId]: { type: 'notification', headline, details, description } });
    }, [errorId, registerError]);

    const handleLoadingIdsChange = useCallback((ids: string[] = selectedIds) => {
        setLoadingIds(ids);
    }, [selectedIds]);

    const isAllSelected = useMemo(() => prompts.length === selectedIds.length && selectedIds.length > 0, [prompts, selectedIds]);

    const onSelectAll = useCallback(() => setSelectedIds(prompts.map(prompt => prompt.key)), [prompts]);
    const onDeselectAll = useCallback(() => setSelectedIds([]), []);

    const onToggleSelectId = useCallback((id: string) => {
        setSelectedIds(s => {
            if (s.includes(id)) return s.filter(x => x !== id);
            return [...s, id];
        });
    }, []);

    return {
        errorId,
        handleErrorRemoval,
        handleErrorRegistering,
        handleLoadingIdsChange,

        prompts,
        setSelectedIds,
        loadingIds,
        selectedIds,
        onToggleSelectId,

        isAllSelected,
        onSelectAll,
        onDeselectAll
    };
};